import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Phone, Lock, User, ArrowRight, ArrowLeft, CheckCircle, Shield, KeyRound, MapPin, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { sendOTP, verifyOTP, resetPassword as resetPasswordApi } from '../services/api';
import { setToken as persistToken, setIsAdmin as persistIsAdmin } from '../services/tokenStore';
import { toast } from 'sonner';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, adminLogin } = useAuth();
  
  // mode: 'login' | 'signup' | 'forgot' | 'admin'
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  useEffect(() => {
    if (searchParams.get('admin') === 'true') setMode('admin');
    if (searchParams.get('mode') === 'register') setMode('signup');
  }, [searchParams]);

  // =================== LOGIN (direct password) ===================
  const handleDirectLogin = async () => {
    if (loading) return;
    if (mobile.length !== 10) { toast.error('Enter valid 10-digit mobile'); return; }
    if (!password) { toast.error('Enter your password'); return; }
    setLoading(true);
    try {
      await login(mobile, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Login failed';
      if (status === 404 || detail === 'User not found') {
        toast.error('Account not found. Please sign up first.');
      } else if (status === 400 && /registration/i.test(detail)) {
        toast.error('Please complete your signup first.');
        setMode('signup');
        setStep(1);
      } else {
        toast.error(detail);
      }
    } finally { setLoading(false); }
  };

  // =================== SIGNUP (OTP flow) ===================
  const handleSendOTP = async () => {
    if (mobile.length !== 10) { toast.error('Enter valid 10-digit mobile'); return; }
    setLoading(true);
    try {
      await sendOTP(mobile);
      toast.success('OTP sent to your mobile!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error('Enter valid 6-digit OTP'); return; }
    setLoading(true);
    try {
      const response = await verifyOTP(mobile, otp);
      if (mode === 'forgot') {
        setStep(3);
        toast.success('OTP verified! Set your new password.');
      } else {
        // Signup flow
        if (response.data.user_exists && response.data.has_password) {
          toast.info('Account already exists. Please login.');
          setMode('login');
          setStep(1);
        } else {
          setStep(3);
          toast.success('OTP verified!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleRegister = async () => {
    if (loading) return;
    if (!name || !email || !city || !pincode || !password || !confirmPassword) { toast.error('Fill all fields'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email address'); return; }
    if (pincode.length !== 6) { toast.error('Enter valid 6-digit pincode'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!agreedToTerms) { toast.error('Please agree to Terms, Privacy & Refund Policy'); return; }
    setLoading(true);
    try {
      await register(mobile, password, name, email, city, pincode);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Registration failed. Please try again.';
      // Account already exists — funnel user to login with their saved mobile.
      if (status === 409) {
        toast.info(detail);
        setMode('login');
        setStep(1);
        setPassword('');
        setConfirmPassword('');
        return;
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  // =================== FORGOT PASSWORD ===================
  const handleResetPassword = async () => {
    if (!password || !confirmPassword) { toast.error('Fill all fields'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await resetPasswordApi(mobile, otp, password);
      toast.success('Password reset successful! You are now logged in.');
      // Auto-login with the returned token
      if (res.data.token) {
        persistToken(res.data.token);
        persistIsAdmin(false);
        window.location.href = '/dashboard';
      } else {
        setMode('login');
        setStep(1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally { setLoading(false); }
  };

  // =================== ADMIN LOGIN ===================
  const handleAdminLogin = async () => {
    if (!adminUsername || !adminPassword) { toast.error('Enter credentials'); return; }
    setLoading(true);
    try {
      await adminLogin(adminUsername, adminPassword);
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally { setLoading(false); }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setStep(1);
    setOtp('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setEmail('');
    setCity('');
    setPincode('');
  };

  // =================== RENDER ===================
  const renderContent = () => {
    // ADMIN LOGIN
    if (mode === 'admin') {
      return (
        <div className="space-y-6 animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#111827] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-medium text-gray-900">Admin Login</h2>
            <p className="text-gray-500 mt-2">Access the admin dashboard</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-username">Username</Label>
              <Input id="admin-username" type="text" value={adminUsername} onChange={(e) => setAdminUsername(e.target.value)} placeholder="Enter admin username" className="input-premium mt-2" data-testid="admin-username-input" />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input id="admin-password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Enter admin password" className="input-premium mt-2" data-testid="admin-password-input" />
            </div>
          </div>
          <Button onClick={handleAdminLogin} disabled={loading} className="btn-premium w-full" data-testid="admin-login-submit-btn">
            {loading ? <div className="spinner" /> : 'Login as Admin'}
          </Button>
          <button onClick={() => switchMode('login')} className="text-gray-500 hover:text-gray-700 w-full text-center text-sm">Back to User Login</button>
        </div>
      );
    }

    // DIRECT LOGIN (password)
    if (mode === 'login') {
      return (
        <div className="space-y-6 animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-medium text-gray-900">Welcome Back!</h2>
            <p className="text-gray-500 mt-2">Login with your mobile & password</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="login-mobile">Mobile Number</Label>
              <div className="flex items-center gap-2 mt-2">
                <div className="h-12 px-4 bg-gray-100 rounded-xl border border-gray-200 flex items-center font-medium text-gray-600">+91</div>
                <Input id="login-mobile" type="tel" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} placeholder="98XX XXX XXX" className="input-premium flex-1" data-testid="login-mobile-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="input-premium mt-2" data-testid="login-password-input"
                onKeyDown={(e) => e.key === 'Enter' && handleDirectLogin()} />
            </div>
          </div>
          <Button onClick={handleDirectLogin} disabled={loading || mobile.length !== 10 || !password} className="btn-premium w-full" data-testid="login-submit-btn">
            {loading ? <div className="spinner" /> : 'Login'}
          </Button>
          <button onClick={() => switchMode('forgot')} className="text-amber-600 hover:text-amber-700 w-full text-center text-sm font-medium" data-testid="forgot-password-link">
            Forgot Password?
          </button>
          <div className="text-center border-t border-gray-200 pt-4">
            <p className="text-gray-500 text-sm">Don't have an account?</p>
            <button onClick={() => switchMode('signup')} className="text-amber-600 hover:text-amber-700 font-semibold text-sm mt-1" data-testid="go-to-signup-btn">
              Sign Up with OTP
            </button>
          </div>
        </div>
      );
    }

    // FORGOT PASSWORD
    if (mode === 'forgot') {
      if (step === 1) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <button onClick={() => switchMode('login')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700"><ArrowLeft className="w-4 h-4" /> Back to Login</button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Forgot Password</h2>
              <p className="text-gray-500 mt-2">Enter your mobile to receive OTP</p>
            </div>
            <div>
              <Label htmlFor="forgot-mobile">Mobile Number</Label>
              <div className="flex items-center gap-2 mt-2">
                <div className="h-12 px-4 bg-gray-100 rounded-xl border border-gray-200 flex items-center font-medium text-gray-600">+91</div>
                <Input id="forgot-mobile" type="tel" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} placeholder="98XX XXX XXX" className="input-premium flex-1" data-testid="forgot-mobile-input" />
              </div>
            </div>
            <Button onClick={handleSendOTP} disabled={loading || mobile.length !== 10} className="btn-premium w-full" data-testid="forgot-send-otp-btn">
              {loading ? <div className="spinner" /> : <> Send OTP <ArrowRight className="w-4 h-4 ml-2" /> </>}
            </Button>
          </div>
        );
      }
      if (step === 2) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700"><ArrowLeft className="w-4 h-4" /> Change Number</button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Enter OTP</h2>
              <p className="text-gray-500 mt-2">Sent to +91 {mobile}</p>
            </div>
            <div>
              <Label htmlFor="forgot-otp">Enter 6-digit OTP</Label>
              <Input id="forgot-otp" type="text" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="input-premium mt-2 text-center text-2xl tracking-widest font-bold" data-testid="forgot-otp-input" />
            </div>
            <Button onClick={handleVerifyOTP} disabled={loading || otp.length !== 6} className="btn-premium w-full" data-testid="forgot-verify-otp-btn">
              {loading ? <div className="spinner" /> : <> Verify OTP <CheckCircle className="w-4 h-4 ml-2" /> </>}
            </Button>
            <button onClick={handleSendOTP} className="text-amber-600 hover:text-amber-700 w-full text-center text-sm font-medium">Resend OTP</button>
          </div>
        );
      }
      if (step === 3) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Set New Password</h2>
              <p className="text-gray-500 mt-2">Create a strong password</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter new password" className="input-premium mt-2" data-testid="new-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm-new-password">Confirm Password</Label>
                <Input id="confirm-new-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="input-premium mt-2" data-testid="confirm-new-password-input" />
              </div>
            </div>
            <Button onClick={handleResetPassword} disabled={loading} className="btn-premium w-full" data-testid="reset-password-btn">
              {loading ? <div className="spinner" /> : 'Reset Password & Login'}
            </Button>
          </div>
        );
      }
    }

    // SIGNUP (OTP flow)
    if (mode === 'signup') {
      if (step === 1) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Create Account</h2>
              <p className="text-gray-500 mt-2">Enter your mobile to get started</p>
            </div>
            <div>
              <Label htmlFor="signup-mobile">Mobile Number</Label>
              <div className="flex items-center gap-2 mt-2">
                <div className="h-12 px-4 bg-gray-100 rounded-xl border border-gray-200 flex items-center font-medium text-gray-600">+91</div>
                <Input id="signup-mobile" type="tel" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} placeholder="98XX XXX XXX" className="input-premium flex-1" data-testid="signup-mobile-input" />
              </div>
            </div>
            <Button onClick={handleSendOTP} disabled={loading || mobile.length !== 10} className="btn-premium w-full" data-testid="signup-send-otp-btn">
              {loading ? <div className="spinner" /> : <> Send OTP <ArrowRight className="w-4 h-4 ml-2" /> </>}
            </Button>
            <div className="text-center border-t border-gray-200 pt-4">
              <p className="text-gray-500 text-sm">Already have an account?</p>
              <button onClick={() => switchMode('login')} className="text-amber-600 hover:text-amber-700 font-semibold text-sm mt-1" data-testid="go-to-login-btn">
                Login with Password
              </button>
            </div>
          </div>
        );
      }
      if (step === 2) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700"><ArrowLeft className="w-4 h-4" /> Change Number</button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Enter OTP</h2>
              <p className="text-gray-500 mt-2">Sent to +91 {mobile}</p>
            </div>
            <div>
              <Label htmlFor="signup-otp">Enter 6-digit OTP</Label>
              <Input id="signup-otp" type="text" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="input-premium mt-2 text-center text-2xl tracking-widest font-bold" data-testid="signup-otp-input" />
            </div>
            <Button onClick={handleVerifyOTP} disabled={loading || otp.length !== 6} className="btn-premium w-full" data-testid="signup-verify-otp-btn">
              {loading ? <div className="spinner" /> : <> Verify OTP <CheckCircle className="w-4 h-4 ml-2" /> </>}
            </Button>
            <button onClick={handleSendOTP} className="text-amber-600 hover:text-amber-700 w-full text-center text-sm font-medium">Resend OTP</button>
          </div>
        );
      }
      if (step === 3) {
        return (
          <div className="space-y-6 animate-fade-in-up">
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">Complete Your Profile</h2>
              <p className="text-gray-500 mt-2">Fill in your details to get started</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" className="input-premium mt-2" data-testid="signup-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address" className="input-premium mt-2" data-testid="signup-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                  <Input id="city" type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Mumbai" className="input-premium mt-2" data-testid="signup-city-input" />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode <span className="text-red-500">*</span></Label>
                  <Input id="pincode" type="text" maxLength={6} value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 400001" className="input-premium mt-2" data-testid="signup-pincode-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="create-password">Password <span className="text-red-500">*</span></Label>
                <Input id="create-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password (min 6 chars)" className="input-premium mt-2" data-testid="signup-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm Password <span className="text-red-500">*</span></Label>
                <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="input-premium mt-2" data-testid="signup-confirm-password-input" />
              </div>
            </div>
            <div className="flex items-start gap-3 py-2">
              <input type="checkbox" id="terms-agree" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-amber-500 rounded" data-testid="terms-checkbox" />
              <label htmlFor="terms-agree" className="text-sm text-gray-600 leading-relaxed">
                I agree to the <Link to="/terms" target="_blank" className="text-amber-600 hover:underline font-medium">Terms of Service</Link>, <Link to="/privacy" target="_blank" className="text-amber-600 hover:underline font-medium">Privacy Policy</Link>, and <Link to="/refund" target="_blank" className="text-amber-600 hover:underline font-medium">Refund Policy</Link>. Membership payments are non-refundable.
              </label>
            </div>
            <Button onClick={handleRegister} disabled={loading || !agreedToTerms} className="btn-premium w-full" data-testid="signup-submit-btn">
              {loading ? <div className="spinner" /> : 'Create Account'}
            </Button>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src="/logo.png" alt="HumanOnRent.AI" className="w-12 h-12 rounded-xl" />
            <span className="font-bold text-2xl bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">HumanOnRent.AI</span>
          </Link>
        </div>
        <div className="premium-card p-8">
          {renderContent()}
        </div>
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-gray-500">
            By continuing, you agree to our <Link to="/terms" className="text-amber-600 hover:underline">Terms</Link> and <Link to="/privacy" className="text-amber-600 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
